import { mainSiteUrl } from '@/mocks/categories';

const steps = [
  { number: '01', title: 'Choose Category', desc: 'Browse through 14+ organized product categories and find exactly what you are looking for in the spreadsheet.' },
  { number: '02', title: 'Review Products', desc: 'Examine curated product listings with QC photos, detailed descriptions, and organized comparison data.' },
  { number: '03', title: 'Compare Information', desc: 'Use side-by-side product comparisons to evaluate quality, pricing, and specifications before deciding.' },
  { number: '04', title: 'Continue Shopping', desc: 'Once you have found your ideal products, head to the main platform to complete your purchase seamlessly.' },
];

const faqs = [
  { q: 'What is a Kakobuy Spreadsheet?', a: 'A Kakobuy Spreadsheet is a curated product database that organizes shopping finds into categorized spreadsheets with QC photos, pricing information, and direct links. It helps shoppers discover quality products efficiently without endless searching.' },
  { q: 'How often is the spreadsheet updated?', a: 'The spreadsheet is updated daily with new product finds, refreshed QC photos, and the latest pricing information to ensure you always have access to current data.' },
  { q: 'Is the Kakobuy Spreadsheet free to use?', a: 'Yes, the spreadsheet is freely accessible. Simply browse the categories and click through to the main platform to view detailed product information and make purchases.' },
  { q: 'How do I use the QC photos?', a: 'QC (Quality Check) photos show real product images taken during inspection. They help you verify the actual quality, color accuracy, and details of products before making a purchase decision.' },
  { q: 'Are the products on the spreadsheet safe to buy?', a: 'All products listed in the spreadsheet go through a quality review process. However, we always recommend reviewing QC photos and product descriptions carefully before purchasing.' },
  { q: 'What categories are available?', a: 'We cover 14 categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and other miscellaneous items.' },
  { q: 'Can I request specific products?', a: 'While we do not take direct product requests, our team continuously updates the spreadsheet based on trending items and popular searches within the community.' },
  { q: 'How is this different from regular searching?', a: 'Unlike regular browsing, the spreadsheet provides organized, pre-vetted product listings with QC data, pricing comparisons, and category filtering &mdash; saving you significant time and effort.' },
  { q: 'Do I need an account to access the spreadsheet?', a: 'No account is required. You can browse all categories and product listings freely. An account may be needed on the main platform for purchases.' },
  { q: 'Is the spreadsheet mobile-friendly?', a: 'Yes, the main platform at kaspreadsheet.com is fully responsive and works seamlessly on mobile devices, tablets, and desktop computers.' },
];

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.sbs';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.a,
    },
  })),
};

export default function ContentSections() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      {/* What Is Section */}
      <section id="guide" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                Getting Started
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
                What Is a Kakobuy Spreadsheet?
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  A <strong>Kakobuy Spreadsheet</strong> is a comprehensive product discovery database designed for smart shoppers who want organized, quality-checked product listings in one convenient location. Instead of spending hours searching through countless listings, the spreadsheet brings curated finds directly to you.
                </p>
                <p>
                  The spreadsheet system organizes products by category, includes <strong>QC (Quality Check) photos</strong>, provides pricing comparisons, and offers direct links to the main platform for seamless shopping. It is the ultimate research tool for anyone looking to find quality products efficiently.
                </p>
                <p>
                  Whether you are searching for the latest sneakers, trendy streetwear, premium accessories, or budget-friendly finds, the <strong>Vigorbuy Spreadsheet</strong> and <strong>Kakobuy Spreadsheet</strong> ecosystem has you covered with regularly updated, organized, and beginner-friendly product databases.
                </p>
              </div>
              <Link
                to="/kakobuy-spreadsheet-guide"
                className="inline-flex items-center gap-2 mt-6 bg-accent-500 hover:bg-accent-600 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Read Complete Guide
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>

            <div className="relative">
              <div
                className="w-full aspect-[4/3] rounded-2xl bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Clean%20organized%20modern%20desk%20workspace%20with%20laptop%20displaying%20spreadsheet%20interface%20surrounded%20by%20curated%20fashion%20lookbook%20pages%20fabric%20swatches%20and%20shopping%20notes%20on%20light%20cream%20surface%20soft%20natural%20daylight%20from%20window%20editorial%20composition&width=800&height=600&seq=what-is-v2-01&orientation=landscape')`,
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              Why Thousands Use the Kakobuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
              See how our organized spreadsheet approach compares to traditional product searching.
            </p>
          </div>

          <div className="max-w-3xl mx-auto overflow-hidden rounded-2xl border border-background-200/60">
            <table className="w-full">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-foreground-800">Feature</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-foreground-400">Normal Search</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-accent-700 bg-accent-50/50">Kakobuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/60">
                {[
                  { feature: 'Product Discovery', normal: 'Slow, scattered', spreadsheet: 'Fast, centralized' },
                  { feature: 'Organization', normal: 'Poor, unstructured', spreadsheet: 'Categorized, clean' },
                  { feature: 'Price Research', normal: 'Manual comparison', spreadsheet: 'Easy side-by-side' },
                  { feature: 'QC Information', normal: 'Limited availability', spreadsheet: 'Included with listings' },
                  { feature: 'Beginner Experience', normal: 'Difficult, confusing', spreadsheet: 'Simple, guided' },
                  { feature: 'Update Frequency', normal: 'Irregular', spreadsheet: 'Daily updates' },
                ].map((row) => (
                  <tr key={row.feature} className="hover:bg-background-100/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-foreground-800">{row.feature}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
                        <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-300">
                          <i className="ri-close-line text-xs"></i>
                        </div>
                        {row.normal}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center bg-accent-50/30">
                      <span className="inline-flex items-center gap-1 text-xs text-accent-700 font-medium">
                        <div className="w-3.5 h-3.5 flex items-center justify-center text-accent-500">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        {row.spreadsheet}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              Simple Process
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              How the Kakobuy Spreadsheet Works
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
              Getting started is easy. Follow these four simple steps to begin discovering products.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div key={step.number} className="relative">
                <div className="bg-background-50 border border-background-200/60 rounded-2xl p-6 h-full">
                  <span className="font-heading text-4xl font-bold text-accent-200 mb-4 block">{step.number}</span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{step.desc}</p>
                </div>
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <div className="w-6 h-6 flex items-center justify-center text-accent-300">
                      <i className="ri-arrow-right-s-line text-xl"></i>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20"
            >
              Start Exploring Now
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Deep Dive Article Teaser */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-accent-50/60 border border-accent-200/40 rounded-2xl p-8 md:p-12">
            <div className="max-w-2xl">
              <span className="inline-block bg-accent-200/70 text-accent-800 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                In-Depth Guides
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                Master Spreadsheet Shopping With Our Expert Guides
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                Dive deep into everything you need to know about using spreadsheets for smarter shopping. From beginner tutorials to advanced product research strategies, our guides cover QC photo analysis, safety practices, platform comparisons, and expert tips for finding hidden gems.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/kakobuy-spreadsheet-guide"
                  className="inline-flex items-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Kakobuy Guide
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
                <Link
                  to="/vigorbuy-spreadsheet"
                  className="inline-flex items-center gap-2 bg-background-50 border border-background-300/70 hover:border-foreground-400 text-foreground-800 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Vigorbuy Guide
                </Link>
                <Link
                  to="/qc-spreadsheet-guide"
                  className="inline-flex items-center gap-2 bg-background-50 border border-background-300/70 hover:border-foreground-400 text-foreground-800 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  QC Photo Guide
                </Link>
                <Link
                  to="/shopping-safety-guide"
                  className="inline-flex items-center gap-2 bg-background-50 border border-background-300/70 hover:border-foreground-400 text-foreground-800 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Safety Guide
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              Common Questions
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
              Everything you need to know about the Kakobuy Spreadsheet and Vigorbuy Spreadsheet ecosystem.
            </p>
          </div>

          <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
            {faqs.map((faq) => (
              <details key={faq.q} className="group bg-background-50 border border-background-200/60 rounded-xl p-5 cursor-pointer">
                <summary className="flex items-start justify-between gap-3 text-sm font-semibold text-foreground-800 list-none">
                  <span>{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-45 transition-transform duration-200 flex-shrink-0 mt-0.5">
                    <i className="ri-add-line"></i>
                  </div>
                </summary>
                <p className="mt-3 text-xs md:text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}